import { useState, useEffect } from 'react';

/**
 * Calculate time remaining until target date
 */
function calculateTimeLeft(targetDate) {
  const now = new Date();
  const difference = targetDate - now;

  if (difference <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((difference / (1000 * 60)) % 60),
    seconds: Math.floor((difference / 1000) % 60),
  };
}

/**
 * FlipCountdown - Animated flip clock countdown timer using CSS custom properties
 * @param {Date} targetDate - The target date to count down to
 * @param {object} labels - Optional labels for days, hours, minutes, seconds
 */
export default function FlipCountdown({
  targetDate,
  labels = { days: 'Days', hours: 'Hours', minutes: 'Min', seconds: 'Sec' }
}) {
  // Initialize with calculated value to avoid flash
  const [timeLeft, setTimeLeft] = useState(() => calculateTimeLeft(targetDate));

  useEffect(() => {
    // Update every second using interval callback
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(targetDate));
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  return (
    <div className="flex justify-center gap-3 sm:gap-5">
      <div className="flex flex-col items-center">
        <span className="countdown font-mono text-3xl sm:text-4xl text-gray-900 dark:text-white">
          <span
            style={{ '--value': timeLeft.days }}
            aria-live="polite"
            aria-label={`${timeLeft.days} ${labels.days}`}
          >
            {timeLeft.days}
          </span>
        </span>
        <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1 uppercase tracking-wider">
          {labels.days}
        </span>
      </div>
      <div className="flex flex-col items-center">
        <span className="countdown font-mono text-3xl sm:text-4xl text-gray-900 dark:text-white">
          <span
            style={{ '--value': timeLeft.hours }}
            aria-live="polite"
            aria-label={`${timeLeft.hours} ${labels.hours}`}
          >
            {timeLeft.hours}
          </span>
        </span>
        <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1 uppercase tracking-wider">
          {labels.hours}
        </span>
      </div>
      <div className="flex flex-col items-center">
        <span className="countdown font-mono text-3xl sm:text-4xl text-gray-900 dark:text-white">
          <span
            style={{ '--value': timeLeft.minutes }}
            aria-live="polite"
            aria-label={`${timeLeft.minutes} ${labels.minutes}`}
          >
            {timeLeft.minutes}
          </span>
        </span>
        <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1 uppercase tracking-wider">
          {labels.minutes}
        </span>
      </div>
      <div className="flex flex-col items-center">
        <span className="countdown font-mono text-3xl sm:text-4xl text-gray-900 dark:text-white">
          <span
            style={{ '--value': timeLeft.seconds }}
            aria-live="polite"
            aria-label={`${timeLeft.seconds} ${labels.seconds}`}
          >
            {timeLeft.seconds}
          </span>
        </span>
        <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1 uppercase tracking-wider">
          {labels.seconds}
        </span>
      </div>
    </div>
  );
}
